import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import MergeOutlinedIcon from '@mui/icons-material/MergeOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useState } from 'react';
import { Can } from '../../../permissions/Can.js';
import { PERMISSIONS } from '../../../permissions/permissionKeys.js';

const toId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value._id) return String(value._id);
  return String(value);
};

const entityEditPathBuilders = {
  competition: (id) => `/sports/competitions/create/${id}`,
  team: (id) => `/sports/teams/create/${id}`,
  player: (id) => `/sports/players/create/${id}`,
  match: (id) => `/match/${id}`,
};

const EntityEditLink = ({ entityType, entityId, children, sx = {} }) => {
  const id = toId(entityId);
  const buildPath = entityEditPathBuilders[entityType];

  if (!id || !buildPath) return children;

  return (
    <Link
      href={buildPath(id)}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        fontWeight: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        ...sx,
      }}
      title="Ouvrir la fiche dans un nouvel onglet"
    >
      {children}
      <OpenInNewOutlinedIcon sx={{ fontSize: '0.9em', opacity: 0.7 }} />
    </Link>
  );
};

const getPlayerName = (player) => {
  const fullName = [player?.firstname, player?.lastname].filter(Boolean).join(' ').trim();
  return fullName || player?.name || 'Joueur sans nom';
};

const formatMatchDate = (value) => {
  if (!value) return 'Date inconnue';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('fr-FR');
};

const entityTypeLabels = {
  competition: 'Compétition',
  team: 'Équipe',
  player: 'Joueur',
  match: 'Match',
  gameFact: 'Fait de jeu',
};

const formatCompetitionContext = (competition) => {
  if (!competition) return 'Compétition inconnue';
  return `${competition?.name || 'Compétition sans nom'}${competition?.season ? ` — ${competition.season}` : ''}`;
};

const PlayerRelationContext = ({ context, compact = false }) => {
  const teams = context?.teams || [];

  if (teams.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Aucun rattachement d'équipe exportée disponible dans le contexte.
      </Typography>
    );
  }

  return (
    <Stack spacing={compact ? 0.5 : 1}>
      {!compact && (
        <Typography variant="caption" color="text.secondary">
          {context?.pathLabel || 'Compétition sélectionnée → Équipe → Player.teamsSeason'}
        </Typography>
      )}

      {teams.map((team) => (
        <Box key={team?.id || team?.name}>
          <Typography variant="body2">
            <strong>Équipe :</strong>{' '}
            <EntityEditLink entityType="team" entityId={team?.id}>
              {team?.name || team?.id || 'Équipe inconnue'}
            </EntityEditLink>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Compétition(s) :</strong>{' '}
            {(team?.competitions || []).length > 0
              ? team.competitions.map((competition, index) => (
                  <Box
                    component="span"
                    key={competition?.id || `${competition?.name}-${index}`}
                  >
                    {index > 0 ? ', ' : ''}
                    <EntityEditLink
                      entityType="competition"
                      entityId={competition?.id}
                    >
                      {formatCompetitionContext(competition)}
                    </EntityEditLink>
                  </Box>
                ))
              : '—'}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

const getErrorSummary = (error) => {
  if (error?.type === 'EXPORT_SCOPE_MISMATCH') {
    return `${error?.name || 'Entité'} : ${error?.field || 'champ'}=${error?.actual ?? 'null'} au lieu de ${error?.expected ?? '—'}.`;
  }

  if (error?.type === 'IDENTITY_COLLISION' && error?.identity) {
    const identity = [
      error.identity.provider,
      error.identity.thirdPartyId,
      error.identity.thirdPartyEntityId,
    ].filter((value) => value !== undefined && value !== null && value !== '').join(' / ');
    const count = error?.conflicts?.length || error?.entityIds?.length || 0;
    return `Identité dupliquée ${identity ? `(${identity}) ` : ''}— ${count} document(s) Mongo.`;
  }

  return (
    error?.message ||
    `champs manquants : ${(error?.missingFields || []).join(', ')}`
  );
};

const ExportErrorDetails = ({ error }) => {
  const [keepPlayerId, setKeepPlayerId] = useState(null);

  const isPlayerCollision =
    error?.type === 'IDENTITY_COLLISION' &&
    error?.entityType === 'player' &&
    (error?.conflicts || []).length > 1;

  const openPlayerMerge = () => {
    if (!isPlayerCollision || !keepPlayerId) return;

    const idsToDelete = error.conflicts
      .map((conflict) => conflict?.entityId)
      .filter(Boolean)
      .filter((id) => id !== keepPlayerId);

    if (idsToDelete.length === 0) return;

    const params = new URLSearchParams();
    params.set('keep', keepPlayerId);
    idsToDelete.forEach((id) => params.append('delete', id));
    params.set('returnTo', '/admin/environment-export');

    window.open(
      `/sports/players/merge/from-export?${params.toString()}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (error?.type === 'IDENTITY_COLLISION' && (error?.conflicts || []).length > 0) {
    return (
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">
          Documents concernés
        </Typography>

        {error.conflicts.map((conflict, index) => (
          <Box
            key={conflict?.entityId || `${conflict?.name}-${index}`}
            sx={{
              pl: 1.5,
              borderLeft: '3px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              <EntityEditLink
                entityType={error?.entityType}
                entityId={conflict?.entityId}
              >
                {conflict?.name || 'Document sans nom'}
              </EntityEditLink>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Mongo id : {conflict?.entityId || '—'}
            </Typography>
            {conflict?.context && (
              <PlayerRelationContext context={conflict.context} compact />
            )}

            {isPlayerCollision && conflict?.entityId && (
              <Can permission={PERMISSIONS.SPORT_PLAYER_FUSION_PAGE}>
                <Button
                  size="small"
                  variant={keepPlayerId === conflict.entityId ? 'contained' : 'outlined'}
                  onClick={() => setKeepPlayerId(conflict.entityId)}
                  sx={{ mt: 1 }}
                >
                  {keepPlayerId === conflict.entityId ? 'Joueur à conserver' : 'Conserver celui-ci'}
                </Button>
              </Can>
            )}
          </Box>
        ))}

        {isPlayerCollision && (
          <Can permission={PERMISSIONS.SPORT_PLAYER_FUSION_PAGE}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                pt: 0.5,
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<MergeOutlinedIcon />}
                endIcon={<OpenInNewOutlinedIcon />}
                disabled={!keepPlayerId}
                onClick={openPlayerMerge}
              >
                Fusionner les joueurs
              </Button>
              <Typography variant="caption" color="text.secondary">
                {keepPlayerId
                  ? 'La fusion s’ouvrira dans un nouvel onglet.'
                  : 'Choisissez d’abord le joueur à conserver.'}
              </Typography>
            </Box>
          </Can>
        )}
      </Stack>
    );
  }

  if (error?.context) {
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle2">
          Rattaché via
        </Typography>
        <PlayerRelationContext context={error.context} />
        {error?.entityId && (
          <Typography variant="caption" color="text.secondary">
            Mongo id : {error.entityId}
          </Typography>
        )}
      </Stack>
    );
  }

  return null;
};

const ExportErrorItem = ({ error, index }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = Boolean(
    error?.context ||
    (Array.isArray(error?.conflicts) && error.conflicts.length > 0)
  );

  return (
    <Box
      sx={{
        py: 0.75,
        '& + &': {
          borderTop: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" sx={{ flex: '1 1 420px' }}>
          • <strong>{entityTypeLabels[error?.entityType] || error?.entityType || 'Entité'}</strong> :{' '}
          {getErrorSummary(error)}
          {error?.entityId && error?.type !== 'IDENTITY_COLLISION' ? ` — id : ${error.entityId}` : ''}
        </Typography>

        {hasDetails && (
          <Button
            size="small"
            variant="text"
            startIcon={expanded ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
            onClick={() => setExpanded((value) => !value)}
            sx={{ minWidth: 'auto', flexShrink: 0 }}
          >
            {expanded ? 'Masquer' : 'Détails'}
          </Button>
        )}
      </Box>

      {hasDetails && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 1,
              ml: { xs: 0, sm: 2 },
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <ExportErrorDetails error={error} />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const EnvironmentExportPreview = ({ result, onDownload }) => {
  if (!result) return null;

  const competitions = result?.data?.competitions || [];
  const teams = result?.data?.teams || [];
  const players = result?.data?.players || [];
  const matches = result?.data?.matches || [];
  const gameFacts = result?.data?.gameFacts || [];
  const errors = result?.errors || [];
  const warnings = result?.warnings || [];
  const valid = result?.valid !== false;

  const teamsById = new Map(teams.map((team) => [toId(team?._id), team]));
  const playersByTeamId = new Map();
  const matchesByCompetitionId = new Map();
  const gameFactsByMatchId = new Map();

  players.forEach((player) => {
    (player?.teams || []).forEach((teamIdValue) => {
      const teamId = toId(teamIdValue);
      if (!teamId) return;
      if (!playersByTeamId.has(teamId)) playersByTeamId.set(teamId, []);
      playersByTeamId.get(teamId).push(player);
    });
  });

  matches.forEach((match) => {
    const competitionId = toId(match?.competition);
    if (!competitionId) return;
    if (!matchesByCompetitionId.has(competitionId)) {
      matchesByCompetitionId.set(competitionId, []);
    }
    matchesByCompetitionId.get(competitionId).push(match);
  });

  gameFacts.forEach((gameFact) => {
    const matchId = toId(gameFact?.idMatch);
    if (!matchId) return;
    if (!gameFactsByMatchId.has(matchId)) gameFactsByMatchId.set(matchId, []);
    gameFactsByMatchId.get(matchId).push(gameFact);
  });

  const resolveCompetitionTeams = (competition) => {
    const competitionId = toId(competition?._id);
    return teams.filter((team) =>
      (team?.competitions || []).map(toId).filter(Boolean).includes(competitionId)
    );
  };

  const getTeamName = (teamIdValue) => {
    const team = teamsById.get(toId(teamIdValue));
    return team?.name || team?.fullName || toId(teamIdValue) || 'Équipe inconnue';
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6">Aperçu de l'export</Typography>
              <Typography variant="body2" color="text.secondary">
                Vue synthétique des entités qui seront transportées vers l'environnement cible.
              </Typography>
            </Box>

            <Button variant="contained" onClick={onDownload} disabled={!valid}>
              Télécharger le JSON
            </Button>
          </Box>

          <Alert severity={valid ? 'success' : 'error'}>
            {valid
              ? 'L’export est valide et peut être téléchargé.'
              : 'L’export contient des erreurs bloquantes. Corrigez-les avant de télécharger le snapshot.'}
          </Alert>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip label={`${result?.summary?.competitions ?? competitions.length} compétition(s)`} />
            <Chip label={`${result?.summary?.teams ?? teams.length} équipe(s)`} />
            <Chip label={`${result?.summary?.players ?? players.length} joueur(s)`} />
            <Chip label={`${result?.summary?.matches ?? matches.length} match(s)`} />
            <Chip label={`${result?.summary?.gameFacts ?? gameFacts.length} fait(s) de jeu`} />
            <Chip label={`${errors.length} erreur(s)`} variant={errors.length ? 'filled' : 'outlined'} />
            <Chip label={`${warnings.length} avertissement(s)`} variant="outlined" />
          </Stack>

          {(errors.length > 0 || warnings.length > 0) && <Divider />}

          {errors.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Erreurs bloquantes
              </Typography>
              {errors.slice(0, 10).map((error, index) => (
                <ExportErrorItem
                  key={`${error?.entityId || error?.type || index}-${index}`}
                  error={error}
                  index={index}
                />
              ))}
              {errors.length > 10 && (
                <Typography variant="body2">… et {errors.length - 10} autre(s).</Typography>
              )}
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Avertissements
              </Typography>
              {warnings.slice(0, 10).map((warning, index) => (
                <Typography key={`${warning?.entityId || index}-${index}`} variant="body2">
                  • {warning?.message || warning?.type || 'Incohérence détectée'}
                </Typography>
              ))}
            </Alert>
          )}

          <Divider />

          <Box>
            {competitions.map((competition) => {
              const competitionId = toId(competition?._id);
              const competitionTeams = resolveCompetitionTeams(competition);
              const competitionMatches = matchesByCompetitionId.get(competitionId) || [];
              const competitionPlayerIds = new Set();
              const competitionGameFactIds = new Set();

              competitionTeams.forEach((team) => {
                (playersByTeamId.get(toId(team?._id)) || []).forEach((player) => {
                  competitionPlayerIds.add(toId(player?._id));
                });
              });

              competitionMatches.forEach((match) => {
                (gameFactsByMatchId.get(toId(match?._id)) || []).forEach((gameFact) => {
                  competitionGameFactIds.add(toId(gameFact?._id));
                });
              });

              return (
                <Accordion key={competitionId || competition?.name} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: '100%' }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        <EntityEditLink
                          entityType="competition"
                          entityId={competition?._id}
                        >
                          {competition?.name || 'Compétition sans nom'}
                          {competition?.season ? ` — ${competition.season}` : ''}
                        </EntityEditLink>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {competitionTeams.length} équipe(s) · {competitionPlayerIds.size} joueur(s) ·{' '}
                        {competitionMatches.length} match(s) · {competitionGameFactIds.size} fait(s) de jeu
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Équipes et joueurs
                        </Typography>

                        <Stack spacing={1}>
                          {competitionTeams.map((team) => {
                            const teamPlayers = playersByTeamId.get(toId(team?._id)) || [];

                            return (
                              <Accordion key={toId(team?._id) || team?.name} variant="outlined" disableGutters>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      <EntityEditLink
                                        entityType="team"
                                        entityId={team?._id}
                                      >
                                        {team?.name || team?.fullName || 'Équipe sans nom'}
                                      </EntityEditLink>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {teamPlayers.length} joueur(s)
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  {teamPlayers.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      Aucun joueur rattaché via Player.teamsSeason dans le périmètre exporté.
                                    </Typography>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                          xs: '1fr',
                                          sm: 'repeat(2, minmax(0, 1fr))',
                                          md: 'repeat(3, minmax(0, 1fr))',
                                        },
                                        gap: 1,
                                      }}
                                    >
                                      {teamPlayers.map((player) => (
                                        <Typography key={toId(player?._id) || getPlayerName(player)} variant="body2">
                                          <EntityEditLink
                                            entityType="player"
                                            entityId={player?._id}
                                          >
                                            {getPlayerName(player)}
                                          </EntityEditLink>
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}

                          {competitionTeams.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              Aucune équipe trouvée pour cette compétition dans le snapshot.
                            </Typography>
                          )}
                        </Stack>
                      </Box>

                      <Divider />

                      <Accordion variant="outlined" disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box>
                            <Typography variant="subtitle2">Matchs</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {competitionMatches.length} match(s) · {competitionGameFactIds.size} fait(s) de jeu
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {competitionMatches.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Aucun match exporté pour cette compétition et cette plage de dates.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {competitionMatches.map((match) => {
                                const matchGameFacts = gameFactsByMatchId.get(toId(match?._id)) || [];

                                return (
                                  <Box
                                    key={toId(match?._id)}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      gap: 2,
                                      flexWrap: 'wrap',
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      py: 1,
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        <EntityEditLink
                                          entityType="match"
                                          entityId={match?._id}
                                        >
                                          {getTeamName(match?.homeTeam)} — {getTeamName(match?.awayTeam)}
                                        </EntityEditLink>
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatMatchDate(match?.dateTime)}
                                        {match?.day !== undefined && match?.day !== null ? ` · journée ${match.day}` : ''}
                                        {match?.status ? ` · ${match.status}` : ''}
                                      </Typography>
                                      {(match?.thirdPartyId || match?.thirdPartyEntityId) && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          Identité source : {match?.thirdPartyId || '—'} / {match?.thirdPartyEntityId || '—'}
                                        </Typography>
                                      )}
                                    </Box>

                                    <Chip
                                      size="small"
                                      label={`${matchGameFacts.length} fait(s) de jeu`}
                                      variant="outlined"
                                    />
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EnvironmentExportPreview;
